import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_BADGE, TEXT_NORMAL, TEXT_SMALL } from "../../style";
import WebsiteMediaInput from "./WebsiteMediaInput";
import FieldNote from "./common/FieldNote";

const SEO_TARGETS = [
    { label: "Whole website and the home page", value: "site:site", owner_type: "site", owner_key: "site", where: "This replaces the home page browser title, and it is the fallback for any page that has no row of its own." },
    { label: "About Us page", value: "page:aboutPage", owner_type: "page", owner_key: "aboutPage", where: "Used on /about-us." },
    { label: "Courses page", value: "page:coursesPage", owner_type: "page", owner_key: "coursesPage", where: "Used on /courses." },
    { label: "Facilities page", value: "page:facilitiesPage", owner_type: "page", owner_key: "facilitiesPage", where: "Used on /facilities." },
    { label: "Notifications page", value: "page:notificationsPage", owner_type: "page", owner_key: "notificationsPage", where: "Used on /notifications." },
    { label: "Gallery page", value: "page:galleryPage", owner_type: "page", owner_key: "galleryPage", where: "Used on /gallery." },
    { label: "e-Brochures page", value: "page:eBrochuresPage", owner_type: "page", owner_key: "eBrochuresPage", where: "Used on /e-brochures." },
    { label: "Contact Us page", value: "page:contactPage", owner_type: "page", owner_key: "contactPage", where: "Used on /contact." },
];

function targetFor(row) {
    return SEO_TARGETS.find((item) => item.owner_type === row?.owner_type && item.owner_key === row?.owner_key);
}

function targetLabel(row) {
    return targetFor(row)?.label || `Not used by the website (${row?.owner_type || "?"} / ${row?.owner_key || "?"})`;
}

export default function WebsiteSeo() {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/seo",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRows(data);
                else setError("Couldn't load SEO");
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Search titles" />
            <TabHeader
                className="px-3 pt-3"
                title="Search titles"
                highlights={[
                    "Every page already has a built-in title and description. Google can read the page without a row here",
                    "Add a row only when you want to replace that built-in text for one page",
                    "A course, a notice, a gallery event, and a brochure already build their title from the record name",
                ]}
                actionItems={[
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: { owner_type: "page", owner_key: "", no_index: false } })} />,
                ]}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    <p className={`${TEXT_SMALL} text-color-secondary m-0 px-2`}>
                        Fill only the words you want to change. A blank field keeps the built-in text. One row per page. Edit the existing row instead of adding a second one for the same page.
                    </p>
                    {rows?.length ? rows.map((row) => (
                        <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} setDialog={setDialog} rows={rows} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </div>
    );
}

function Row({ row, setRows, requestAPI, showToast, openEdit }) {
    const [deleting, setDeleting] = useState();
    const known = !!targetFor(row);
    const remove = () => {
        requestAPI({
            requestPath: `website/seo/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Search title deleted", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
                }
            },
        });
    };
    return (
        <div className={`flex gap-3 align-items-center border-round py-2 px-3 text-white ${known ? "bg-green-500" : "bg-red-500"}`}>
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{targetLabel(row)}</span>
                {row.title ? <span className={`${TEXT_BADGE} white-space-nowrap overflow-hidden text-overflow-ellipsis`}>{row.title}</span> : null}
            </div>
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, setDialog, rows, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ no_index: false, ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const currentValue = record.owner_type && record.owner_key ? `${record.owner_type}:${record.owner_key}` : "";
    const known = SEO_TARGETS.some((item) => item.value === currentValue);
    const options = known || !currentValue ? SEO_TARGETS : [{ label: targetLabel(record), value: currentValue, owner_type: record.owner_type, owner_key: record.owner_key, where: "This page key is not used by the website. Pick one of the pages above if you want the text to appear." }, ...SEO_TARGETS];
    const selected = options.find((item) => item.value === currentValue);
    const save = () => {
        const target = SEO_TARGETS.find((item) => item.value === currentValue) || (currentValue ? { owner_type: record.owner_type, owner_key: record.owner_key } : null);
        if (!target?.owner_type || !target?.owner_key) {
            showToast({ severity: "error", summary: "Choose a page", detail: "Pick the page this search title is for.", life: 2000 });
            return;
        }
        const duplicate = (rows || []).find((row) => row.id !== record.id && row.owner_type === target.owner_type && row.owner_key === target.owner_key);
        if (duplicate) {
            showToast({ severity: "error", summary: "Already added", detail: "That page already has a search title. Edit that row.", life: 2500 });
            return;
        }
        requestAPI({
            requestPath: "website/seo",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: {
                ...record,
                owner_type: target.owner_type,
                owner_key: target.owner_key,
                no_index: record.no_index === true || record.no_index === 1,
            },
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: "Search title saved", life: 1000 });
                    setDialog({ visible: false });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
                }
            },
        });
    };
    return (
        <Dialog header={isNew ? "Add a search title" : "Edit search title"} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                <div>
                    <FloatLabel className="mt-5">
                        <Dropdown inputId="page" className="w-full" options={options} optionLabel="label" optionValue="value" value={currentValue || null} onChange={(event) => {
                            const next = SEO_TARGETS.find((item) => item.value === event.value) || options.find((item) => item.value === event.value);
                            setRecord((prev) => ({ ...prev, owner_type: next?.owner_type || "", owner_key: next?.owner_key || "" }));
                        }} disabled={saving} />
                        <label htmlFor="page">Page</label>
                    </FloatLabel>
                    <FieldNote text={selected?.where || "Pick the page. This row changes only that page. It does not change the words visitors see on the page itself."} />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="title">Search title</label>
                    </FloatLabel>
                    <FieldNote text="The blue link in Google, and the browser tab. Leave blank to keep the built-in title." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="description" className="w-full" rows={3} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={saving} />
                        <label htmlFor="description">Search description</label>
                    </FloatLabel>
                    <FieldNote text="The grey lines under the link in Google. Leave blank to keep the built-in description." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="keywords" className="w-full" value={record?.keywords ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, keywords: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="keywords">Extra search words</label>
                    </FloatLabel>
                    <FieldNote text="Optional. Most search engines ignore this. Leave blank to keep the built-in words." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="canonical_url" className="w-full" value={record?.canonical_url ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, canonical_url: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="canonical_url">Official page address</label>
                    </FloatLabel>
                    <FieldNote text="Leave blank. The site already uses the normal address, such as https://www.sahasinstitute.com/courses. Fill this only when Google should treat a different address as the official one." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="og_title" className="w-full" value={record?.og_title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, og_title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="og_title">Share title</label>
                    </FloatLabel>
                    <FieldNote text="The title WhatsApp and Facebook show. Leave blank to use the search title." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="og_description" className="w-full" rows={3} autoResize value={record?.og_description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, og_description: event.target.value }))} disabled={saving} />
                        <label htmlFor="og_description">Share description</label>
                    </FloatLabel>
                    <FieldNote text="The line under the share title. Leave blank to use the search description." />
                </div>
                <WebsiteMediaInput label="Share image" field="og_image" hint="Required size: 1200 ? 630 px. This is the picture WhatsApp and Facebook show. Leave blank to use the site share image, or the site logo." record={record} setRecord={setRecord} disabled={saving} />
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="twitter_title" className="w-full" value={record?.twitter_title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, twitter_title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="twitter_title">X title</label>
                    </FloatLabel>
                    <FieldNote text="Optional. Leave blank and X uses the share title." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="twitter_description" className="w-full" rows={3} autoResize value={record?.twitter_description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, twitter_description: event.target.value }))} disabled={saving} />
                        <label htmlFor="twitter_description">X description</label>
                    </FloatLabel>
                    <FieldNote text="Optional. Leave blank and X uses the share description." />
                </div>
                <div className="flex align-items-center gap-2 mt-4">
                    <Checkbox inputId="no_index" checked={record?.no_index === true || record?.no_index === 1} onChange={(event) => setRecord((prev) => ({ ...prev, no_index: event.checked }))} disabled={saving} />
                    <label htmlFor="no_index" className={TEXT_SMALL}>Hide this page from Google</label>
                </div>
                <FieldNote text="Leave this off. Turn it on only when this page should not appear in search results." />
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
